import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdCheck, MdClose, MdEdit, MdSave } from 'react-icons/md';

import Card from 'components/card';
import ConfirmModal from 'components/modals/ConfirmModal';
import {
    getPreparationById,
    validatePreparation,
    cancelPreparation,
    updatePreparationStatus,
    updatePreparationLine,
    updatePreparationNotes,
    getProductInventory,
} from 'interfaces/orderPreparation';
import { IOrderPreparation, IPreparationLine, IPreparationInventoryLine } from 'interfaces/orderPreparationTypes';
import { dateToFrench } from 'utils/date';

const statusLabels = {
    draft: 'Brouillon',
    in_progress: 'En cours',
    completed: 'Terminée',
    cancelled: 'Annulée'
};

const statusColors = {
    draft: 'bg-gray-500',
    in_progress: 'bg-[#0260CB]',
    completed: 'bg-green-500',
    cancelled: 'bg-red-500'
};

const ViewPreparation = () => {
    const { id } = useParams<{ id: string }>();
    const nav = useNavigate();
    const [preparation, setPreparation] = useState<IOrderPreparation | null>(null);
    const [loading, setLoading] = useState(true);
    const [editingNotes, setEditingNotes] = useState(false);
    const [notes, setNotes] = useState('');
    const [editingInventory, setEditingInventory] = useState<{
        productId: string;
        inventoryLines: IPreparationInventoryLine[];
    } | null>(null);
    const [availableInventory, setAvailableInventory] = useState<any[]>([]);
    const [productInventories, setProductInventories] = useState<{[productId: string]: any[]}>({});
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [showValidateModal, setShowValidateModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [validateModalContent, setValidateModalContent] = useState<{
        title: string;
        description: React.ReactNode;
        isWarning: boolean;
    }>({
        title: '',
        description: '',
        isWarning: false
    });

    /**
     * Sort preparation lines by category and subcategory position
     * Products without category/subcategory are placed at the end
     */
    const sortPreparationLines = (lines: IPreparationLine[]): IPreparationLine[] => {
        return [...lines].sort((a, b) => {
            // Products with subcategory
            const aHasSubCat = !!a.subCategory;
            const bHasSubCat = !!b.subCategory;
            
            // Products with category (but no subcategory)
            const aHasCat = !!a.category && !aHasSubCat;
            const bHasCat = !!b.category && !bHasSubCat;
            
            // Products without any category
            const aHasNone = !a.category && !a.subCategory;
            const bHasNone = !b.category && !b.subCategory;
            
            // Products without category go to the end
            if (aHasNone && !bHasNone) return 1;
            if (!aHasNone && bHasNone) return -1;
            if (aHasNone && bHasNone) return 0;
            
            // Both have subcategory - sort by category position, then subcategory position
            if (aHasSubCat && bHasSubCat) {
                // First compare parent category position
                const aCatPos = a.category?.position ?? 999999;
                const bCatPos = b.category?.position ?? 999999;
                
                if (aCatPos !== bCatPos) {
                    return aCatPos - bCatPos;
                }
                
                // Same parent category, compare subcategory position
                const aSubCatPos = a.subCategory?.position ?? 999999;
                const bSubCatPos = b.subCategory?.position ?? 999999;
                
                return aSubCatPos - bSubCatPos;
            }
            
            // One has subcategory, one has only category
            if (aHasSubCat && bHasCat) {
                const aCatPos = a.category?.position ?? 999999;
                const bCatPos = b.category?.position ?? 999999;
                
                if (aCatPos !== bCatPos) {
                    return aCatPos - bCatPos;
                }
                // Same category - product with subcategory comes first
                return -1;
            }
            
            if (aHasCat && bHasSubCat) {
                const aCatPos = a.category?.position ?? 999999;
                const bCatPos = b.category?.position ?? 999999;
                
                if (aCatPos !== bCatPos) {
                    return aCatPos - bCatPos;
                }
                // Same category - product with subcategory comes first
                return 1;
            }
            
            // Both have only category - sort by category position
            if (aHasCat && bHasCat) {
                const aCatPos = a.category?.position ?? 999999;
                const bCatPos = b.category?.position ?? 999999;
                
                return aCatPos - bCatPos;
            }
            
            return 0;
        });
    };

    const fetchPreparation = async () => {
        if (!id) return;
        setLoading(true);
        const res = await getPreparationById(id);
        if (res) {
            setPreparation(res);
            setNotes(res.notes || '');
            
            // Load inventory only for products with insufficient quantity
            const insufficientProducts = res.preparationLines.filter(
                (line: IPreparationLine) => line.quantityPrepared < line.quantityOrdered
            );
            
            if (insufficientProducts.length > 0) {
                const inventoryPromises = insufficientProducts.map(async (line: IPreparationLine) => {
                    const inventory = await getProductInventory(line.productId);
                    return { productId: line.productId, inventory: inventory || [] };
                });
                
                const inventories = await Promise.all(inventoryPromises);
                const inventoryMap: {[key: string]: any[]} = {};
                inventories.forEach(item => {
                    inventoryMap[item.productId] = item.inventory;
                });
                setProductInventories(inventoryMap);
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchPreparation();
    }, [id]);

    // Auto-save inventory lines with debounce
    useEffect(() => {
        if (!editingInventory || !id) return;
        
        setSaveStatus('saving');
        const delayDebounceFn = setTimeout(async () => {
            // Validate that all required fields are filled
            // For MANUAL entries, we allow saving even if area/lot are empty (user might be filling them)
            const hasEmptyFields = editingInventory.inventoryLines.some(
                inv => !inv.inventoryId || inv.quantityPicked <= 0
            );
            
            if (hasEmptyFields) {
                setSaveStatus('idle');
                return;
            }
            
            // Filter out any lines with empty inventoryId and prepare data
            const validLines = editingInventory.inventoryLines
                .filter(inv => inv.inventoryId && inv.inventoryId.trim() !== '')
                .map(inv => ({
                    inventoryId: inv.inventoryId,
                    area: inv.area || 'N/A', // Default to 'N/A' if not specified
                    lotNumber: inv.lotNumber || undefined,
                    dlc: inv.dlc || undefined,
                    quantityPicked: inv.quantityPicked,
                    quantityAvailable: inv.quantityAvailable
                }));
            
            const res = await updatePreparationLine(id, editingInventory.productId, {
                inventoryLines: validLines
            });
            
            if (res) {
                setSaveStatus('saved');
                // Refresh preparation in background without closing edit mode
                const updatedPrep = await getPreparationById(id);
                if (updatedPrep) {
                    setPreparation(updatedPrep);
                }
            } else {
                setSaveStatus('error');
            }
        }, 1500);
        
        return () => clearTimeout(delayDebounceFn);
    }, [editingInventory, id]);

    const handleValidate = async () => {
        if (!preparation) return;
        
        // Check for incomplete products
        const incompleteProducts = preparation.preparationLines.filter(
            (line: IPreparationLine) => line.quantityPrepared < line.quantityOrdered
        );
        
        if (incompleteProducts.length > 0) {
            const productList = (
                <div className="mt-3">
                    <p className="mb-3 font-semibold">Les produits suivants n'ont pas été complètement préparés :</p>
                    <ul className="list-disc list-inside space-y-2 mb-4 text-sm">
                        {incompleteProducts.map((line: IPreparationLine, idx: number) => {
                            const missing = line.quantityOrdered - line.quantityPrepared;
                            return (
                                <li key={idx} className="text-orange-600">
                                    <strong>{line.productName}</strong> - manque <strong>{missing} {line.unity}</strong>
                                </li>
                            );
                        })}
                    </ul>
                    <p className="text-sm">
                        En validant, vous <strong>modifierez la commande client</strong> avec les quantités réellement préparées.
                    </p>
                </div>
            );
            
            setValidateModalContent({
                title: 'ATTENTION !',
                description: productList,
                isWarning: true
            });
        } else {
            setValidateModalContent({
                title: 'Valider la préparation',
                description: 'Êtes-vous sûr de vouloir valider cette préparation ? Cette action est irréversible.',
                isWarning: false
            });
        }
        
        setShowValidateModal(true);
    };

    const confirmValidate = async () => {
        if (!id) return;
        const res = await validatePreparation(id);
        if (res) {
            fetchPreparation();
        }
    };

    const handleCancel = () => {
        setShowCancelModal(true);
    };

    const confirmCancel = async () => {
        if (!id) return;
        const res = await cancelPreparation(id);
        if (res) {
            fetchPreparation();
        }
    };

    const handleChangeStatus = async (status: string) => {
        if (!id) return;
        const res = await updatePreparationStatus(id, status);
        if (res) {
            fetchPreparation();
        }
    };

    const handleSaveNotes = async () => {
        if (!id) return;
        const res = await updatePreparationNotes(id, notes);
        if (res) {
            setEditingNotes(false);
            fetchPreparation();
        }
    };

    const handleEditInventoryLines = (productId: string, inventoryLines: IPreparationInventoryLine[]) => {
        setEditingInventory({
            productId,
            inventoryLines: JSON.parse(JSON.stringify(inventoryLines)) // Deep copy
        });
        
        // Load available inventory for this product
        loadProductInventory(productId);
    };

    const loadProductInventory = async (productId: string) => {
        const inventory = await getProductInventory(productId);
        if (inventory) {
            setAvailableInventory(inventory);
        }
    };

    const handleCancelEditInventory = () => {
        setEditingInventory(null);
        setSaveStatus('idle');
    };

    const handleUpdateInventoryLine = (invIndex: number, field: keyof IPreparationInventoryLine, value: any) => {
        if (!editingInventory) return;
        
        const updatedLines = [...editingInventory.inventoryLines];
        updatedLines[invIndex] = {
            ...updatedLines[invIndex],
            [field]: value
        };
        
        setEditingInventory({
            ...editingInventory,
            inventoryLines: updatedLines
        });
    };

    const handleAddInventoryLine = () => {
        if (!editingInventory || !preparation) return;
        
        // Find the current product line to get quantityOrdered
        const currentLine = preparation.preparationLines.find(
            line => line.productId === editingInventory.productId
        );
        
        if (!currentLine) return;
        
        // Calculate how much is still needed
        const currentTotal = editingInventory.inventoryLines.reduce(
            (sum, inv) => sum + (inv.quantityPicked || 0), 
            0
        );
        const quantityNeeded = currentLine.quantityOrdered - currentTotal;
        
        // Get already selected inventory IDs
        const selectedIds = editingInventory.inventoryLines
            .map(line => line.inventoryId)
            .filter(id => id && id.trim() !== '');
        
        // Find the first available inventory not already selected (FIFO - oldest first)
        const nextAvailableInventory = availableInventory.find(
            stock => !selectedIds.includes(stock.id)
        );
        
        let newLine: IPreparationInventoryLine;
        
        if (nextAvailableInventory && quantityNeeded > 0) {
            // Pre-fill with the oldest available lot
            const quantityToPick = Math.min(quantityNeeded, nextAvailableInventory.quantity);
            
            newLine = {
                inventoryId: nextAvailableInventory.id,
                area: nextAvailableInventory.area,
                lotNumber: nextAvailableInventory.lotNumber || '',
                dlc: nextAvailableInventory.dlc || '',
                quantityPicked: quantityToPick,
                quantityAvailable: nextAvailableInventory.quantity
            };
        } else {
            // No more available inventory or nothing needed - initialize with manual entry
            newLine = {
                inventoryId: 'MANUAL',
                area: '',
                lotNumber: '',
                dlc: '',
                quantityPicked: quantityNeeded > 0 ? quantityNeeded : 0,
                quantityAvailable: 9999
            };
        }
        
        setEditingInventory({
            ...editingInventory,
            inventoryLines: [...editingInventory.inventoryLines, newLine]
        });
    };

    const handleRemoveInventoryLine = (invIndex: number) => {
        if (!editingInventory) return;
        
        const updatedLines = editingInventory.inventoryLines.filter((_, idx) => idx !== invIndex);
        
        setEditingInventory({
            ...editingInventory,
            inventoryLines: updatedLines
        });
    };

    const isEditable = preparation?.status === 'draft' || preparation?.status === 'in_progress';

    if (loading) {
        return (
            <div className="mt-3 flex h-[600px] items-center justify-center">
                <div className="text-center">
                    <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-toola-500 border-r-transparent"></div>
                    <p className="text-gray-600">Chargement de la préparation...</p>
                </div>
            </div>
        );
    }

    if (!preparation) {
        return (
            <div className="mt-3 flex h-[600px] items-center justify-center">
                <Card extra="p-8">
                    <p className="text-xl font-semibold text-gray-600">Préparation introuvable</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="mt-3 h-full">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => nav('/preparations')}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-lightPrimary text-[#0260CB] transition duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10"
                    >
                        <MdArrowBack className="h-5 w-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
                            Préparation - {preparation.orderNumber}
                            {preparation.customerName && (
                                <span className="ml-2 text-xl font-semibold text-[#0260CB]">
                                    - {preparation.customerName}
                                </span>
                            )}
                        </h1>
                        <p className="text-sm text-gray-600">
                            Créée le {dateToFrench(preparation.createdAt)}
                        </p>
                        {preparation.deliveryNote && (
                            <div className="mt-1 flex items-start gap-2">
                                <p className="text-sm font-medium text-gray-700">Note de livraison :</p>
                                <p className="text-sm text-gray-600 italic">{preparation.deliveryNote}</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {isEditable && (
                        <>
                            {preparation.status === 'draft' && (
                                <button
                                    onClick={() => handleChangeStatus('in_progress')}
                                    className="linear flex items-center gap-2 rounded-xl bg-[#0260CB] px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-[#01499b] active:bg-[#024086]"
                                >
                                    Démarrer la préparation
                                </button>
                            )}
                            <button
                                onClick={handleValidate}
                                className="linear flex items-center gap-2 rounded-xl bg-green-500 px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-green-600 active:bg-green-700"
                            >
                                <MdCheck className="h-5 w-5" />
                                Valider
                            </button>
                            <button
                                onClick={handleCancel}
                                className="linear flex items-center gap-2 rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-red-600 active:bg-red-700"
                            >
                                <MdClose className="h-5 w-5" />
                                Annuler
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Status and Info Card */}
            <Card extra="mb-5 p-6 !border !border-[#7090B0]/[0.25]">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <p className="text-sm font-medium text-gray-600">Statut</p>
                        <span className={`mt-1 inline-block rounded-xl px-3 py-1 text-sm font-medium text-white ${statusColors[preparation.status as keyof typeof statusColors]}`}>
                            {statusLabels[preparation.status as keyof typeof statusLabels]}
                        </span>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-gray-600">Produits</p>
                        <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white">
                            {preparation.preparationLines.length}
                        </p>
                    </div>

                    {preparation.preparedBy && (
                        <div>
                            <p className="text-sm font-medium text-gray-600">Préparateur</p>
                            <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white">
                                {preparation.preparedBy.fullName}
                            </p>
                        </div>
                    )}

                    {preparation.validatedBy && (
                        <div>
                            <p className="text-sm font-medium text-gray-600">Validé par</p>
                            <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white">
                                {preparation.validatedBy.fullName}
                            </p>
                        </div>
                    )}
                </div>

                {/* Comment */}
                <div className="mt-6">
                    <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-600">Notes générales</p>
                        {isEditable && !editingNotes && (
                            <button
                                onClick={() => setEditingNotes(true)}
                                className="text-[#0260CB] hover:text-[#01499b]"
                            >
                                <MdEdit className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    {editingNotes ? (
                        <div className="flex gap-2">
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="flex-1 rounded-xl border border-gray-300 p-2 text-sm outline-none focus:border-[#0260CB]"
                                rows={3}
                                placeholder="Ajoutez des notes..."
                            />
                            <div className="flex flex-col gap-2">
                                <button
                                    onClick={handleSaveNotes}
                                    className="rounded-xl bg-[#0260CB] p-2 text-white hover:bg-[#01499b]"
                                >
                                    <MdSave className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingNotes(false);
                                        setNotes(preparation.notes || '');
                                    }}
                                    className="rounded-xl bg-gray-300 p-2 text-gray-700 hover:bg-gray-400"
                                >
                                    <MdClose className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="text-navy-700 dark:text-white">
                            {preparation.notes || 'Aucune note'}
                        </p>
                    )}
                </div>
            </Card>

            {/* Preparation Lines */}
            <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
                Lignes de préparation
            </h2>

            <div className="space-y-4">
                {(() => {
                    const sortedLines = sortPreparationLines(preparation.preparationLines);
                    let currentCategory: string | null = null;
                    let currentSubCategory: string | null = null;
                    
                    return sortedLines.map((line: IPreparationLine, index: number) => {
                        const showCategoryHeader = line.category && line.category.id !== currentCategory;
                        const showSubCategoryHeader = line.subCategory && line.subCategory.id !== currentSubCategory;
                        
                        if (showCategoryHeader) {
                            currentCategory = line.category!.id;
                            currentSubCategory = null; // Reset subcategory when category changes
                        }
                        
                        if (showSubCategoryHeader) {
                            currentSubCategory = line.subCategory!.id;
                        }
                        
                        return (
                            <div key={index}>
                                {/* Category Header */}
                                {showCategoryHeader && (
                                    <div className="mb-3 mt-6 first:mt-0">
                                        <h3 className="text-lg font-bold text-[#0260CB] border-b-2 border-[#0260CB] pb-2">
                                            {line.category!.name}
                                        </h3>
                                    </div>
                                )}
                                
                                {/* SubCategory Header */}
                                {showSubCategoryHeader && (
                                    <div className="mb-2 mt-4">
                                        <h4 className="text-md font-semibold text-gray-700 pl-3 border-l-4 border-[#7090B0]">
                                            {line.subCategory!.name}
                                        </h4>
                                    </div>
                                )}
                                
                                {/* Product Card */}
                                <Card extra="p-6 !border !border-[#7090B0]/[0.25]">
                        <div className="mb-4 flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                                    {line.productName}
                                </h3>
                                {line.productRef && (
                                    <p className="text-sm text-gray-600">Réf: {line.productRef}</p>
                                )}
                                
                                {/* Packaging or Unity */}
                                <div className="mt-1">
                                    {line.packaging && (line.packaging.ref || line.packaging.unity) ? (
                                        <p className="text-sm text-gray-600">
                                             <span className="font-medium">Conditionnement:</span> {line.packaging.unity || line.packaging.ref}
                                            {line.packaging.weight && <span className="text-gray-500"> ({line.packaging.weight} {line.unity})</span>}
                                        </p>
                                    ) : (
                                        <p className="text-sm text-gray-600">
                                            📏 <span className="font-medium">Unité:</span> {line.unity}
                                        </p>
                                    )}
                                </div>
                                
                                {/* Product Comment */}
                                {line.productComment && (
                                    <div className="mt-2 rounded-lg bg-yellow-50 p-2 text-sm">
                                        <p className="font-medium text-yellow-800">Commentaire produit :</p>
                                        <p className="mt-1 text-yellow-700">{line.productComment}</p>
                                    </div>
                                )}
                                
                                {/* Display category and subcategory */}
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {line.category && (
                                        <span className="inline-flex items-center rounded-lg bg-[#0260CB]/10 px-2 py-1 text-xs font-medium text-[#0260CB]">
                                             {line.category.name}
                                        </span>
                                    )}
                                    {line.subCategory && (
                                        <span className="inline-flex items-center rounded-lg bg-[#7090B0]/20 px-2 py-1 text-xs font-medium text-gray-700">
                                             {line.subCategory.name}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-gray-600">Commandé</p>
                                <p className="text-lg font-bold text-navy-700 dark:text-white">
                                    {line.packaging && line.packaging.unity ? (
                                        <>
                                            {line.quantityOrdered} {line.packaging.unity}
                                            <span className="ml-2 text-sm text-gray-500">({line.quantityOrdered * (line.packaging.weight || 1)} {line.unity})</span>
                                        </>
                                    ) : (
                                        <>{line.quantityOrdered} {line.unity}</>
                                    )}
                                </p>
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="mb-2 flex items-center justify-between">
                                <p className="font-medium text-gray-700">Quantité préparée</p>
                                <p className={`text-lg font-bold ${
                                    line.quantityPrepared >= line.quantityOrdered 
                                        ? 'text-green-600' 
                                        : 'text-orange-600'
                                }`}>
                                    {line.packaging && line.packaging.unity ? (
                                        <>
                                            {line.quantityPrepared} {line.packaging.unity}
                                            <span className="ml-2 text-sm text-gray-500">({line.quantityPrepared * (line.packaging.weight || 1)} {line.unity})</span>
                                        </>
                                    ) : (
                                        <>{line.quantityPrepared} {line.unity}</>
                                    )}
                                </p>
                            </div>
                            {line.quantityPrepared < line.quantityOrdered && (
                                <>
                                    <div className="rounded-lg bg-orange-50 p-2 text-sm text-orange-600">
                                        ⚠️ Quantité insuffisante ({
                                            line.packaging && line.packaging.unity ? (
                                                <>
                                                    {line.quantityOrdered - line.quantityPrepared} {line.packaging.unity} manquants
                                                    <span className="text-xs"> = {(line.quantityOrdered - line.quantityPrepared) * (line.packaging.weight || 1)} {line.unity}</span>
                                                </>
                                            ) : (
                                                <>{line.quantityOrdered - line.quantityPrepared} {line.unity} manquants</>
                                            )
                                        })
                                    </div>
                                    {productInventories[line.productId] && productInventories[line.productId].length > 0 && (
                                        <div className="mt-2 rounded-lg bg-[#0260CB]/10 p-3">
                                            <p className="mb-2 text-xs font-medium text-[#0260CB]">Stocks disponibles :</p>
                                            <div className="space-y-1">
                                                {productInventories[line.productId].map((stock: any, idx: number) => (
                                                    <div key={idx} className="flex items-center justify-between text-xs text-[#0260CB]">
                                                        <span>
                                                            <span className="font-medium">Zone {stock.area}</span>
                                                            {stock.lotNumber && <span className="ml-2">• Lot: {stock.lotNumber}</span>}
                                                            {stock.dlc && <span className="ml-2">• DLC: {dateToFrench(stock.dlc)}</span>}
                                                        </span>
                                                        <span className="font-medium">{stock.quantity} {line.unity}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Inventory Lines Table */}
                        <div className="overflow-x-auto">
                            {editingInventory && editingInventory.productId === line.productId ? (
                                // Edition mode
                                <div className="space-y-3">
                                    <div className="rounded-lg bg-[#0260CB]/10 p-3 text-sm text-[#0260CB]">
                                        💡 <strong>Plusieurs lots disponibles ?</strong> Utilisez le bouton "+ Ajouter ligne" pour picker le même produit avec différents lots/zones de stock.
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="font-medium text-navy-700">Édition des lignes d'inventaire</h4>
                                            <p className="mt-1 text-sm text-gray-600">
                                                Total à prélever: {' '}
                                                <span className={`font-bold ${
                                                    editingInventory.inventoryLines.reduce((sum, inv) => sum + (inv.quantityPicked || 0), 0) >= line.quantityOrdered
                                                        ? 'text-green-600' 
                                                        : 'text-orange-600'
                                                }`}>
                                                    {editingInventory.inventoryLines.reduce((sum, inv) => sum + (inv.quantityPicked || 0), 0)} / {line.quantityOrdered} {line.unity}
                                                </span>
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {saveStatus === 'saving' && (
                                                <span className="text-sm text-gray-600">
                                                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-orange-500 mr-2"></span>
                                                    Sauvegarde...
                                                </span>
                                            )}
                                            {saveStatus === 'saved' && (
                                                <span className="text-sm text-green-600">
                                                    <MdCheck className="inline h-4 w-4 mr-1" />
                                                    Sauvegardé
                                                </span>
                                            )}
                                            {saveStatus === 'error' && (
                                                <span className="text-sm text-red-600">
                                                    <MdClose className="inline h-4 w-4 mr-1" />
                                                    Erreur
                                                </span>
                                            )}
                                            <button
                                                onClick={handleAddInventoryLine}
                                                className="linear rounded-lg bg-green-500 px-3 py-1 text-sm text-white transition duration-200 hover:bg-green-600 active:bg-green-700"
                                            >
                                                + Ajouter ligne
                                            </button>
                                            <button
                                                onClick={handleCancelEditInventory}
                                                className="linear rounded-lg bg-gray-500 px-3 py-1 text-sm text-white transition duration-200 hover:bg-gray-600 active:bg-gray-700"
                                            >
                                                Fermer
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {editingInventory.inventoryLines.map((inv, invIndex) => (
                                        <div key={invIndex} className="rounded-lg border border-gray-200 p-3">
                                            <div className="mb-2 flex items-center justify-between">
                                                <span className="text-sm font-medium text-gray-600">Ligne {invIndex + 1}</span>
                                                <button
                                                    onClick={() => handleRemoveInventoryLine(invIndex)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    ✕ Supprimer
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                                <div className="md:col-span-2">
                                                    <label className="mb-1 block text-xs text-gray-600">Sélectionner le stock *</label>
                                                    <select
                                                        value={inv.inventoryId || ''}
                                                        onChange={(e) => {
                                                            if (!editingInventory) return;
                                                            
                                                            const selectedValue = e.target.value;
                                                            
                                                            if (selectedValue === 'MANUAL') {
                                                                // Manual
                                                                const updatedLines = [...editingInventory.inventoryLines];
                                                                updatedLines[invIndex] = {
                                                                    ...updatedLines[invIndex],
                                                                    inventoryId: 'MANUAL',
                                                                    area: '',
                                                                    lotNumber: '',
                                                                    dlc: '',
                                                                    quantityAvailable: 9999,
                                                                };
                                                                
                                                                setEditingInventory({
                                                                    ...editingInventory,
                                                                    inventoryLines: updatedLines
                                                                });
                                                            } else {
                                                                const selectedInventory = availableInventory.find(i => i.id === selectedValue);
                                                                if (selectedInventory) {
                                                                    const updatedLines = [...editingInventory.inventoryLines];
                                                                    updatedLines[invIndex] = {
                                                                        ...updatedLines[invIndex],
                                                                        inventoryId: selectedInventory.id,
                                                                        area: selectedInventory.area,
                                                                        lotNumber: selectedInventory.lotNumber || '',
                                                                        dlc: selectedInventory.dlc || '',
                                                                        quantityAvailable: selectedInventory.quantity,
                                                                    };
                                                                    
                                                                    setEditingInventory({
                                                                        ...editingInventory,
                                                                        inventoryLines: updatedLines
                                                                    });
                                                                }
                                                            }
                                                        }}
                                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                    >
                                                        <option value="">-- Choisir un stock --</option>
                                                        <option value="MANUAL" className="font-semibold text-[#0260CB]">Saisie manuelle (sans stock)</option>
                                                        {availableInventory
                                                            .filter(stock => {
                                                                // Show stock if not already selected in another line (except current line)
                                                                const alreadySelected = editingInventory.inventoryLines.some(
                                                                    (line, idx) => idx !== invIndex && line.inventoryId === stock.id
                                                                );
                                                                return !alreadySelected;
                                                            })
                                                            .map((stock) => (
                                                            <option key={stock.id} value={stock.id}>
                                                                {stock.area} | Lot: {stock.lotNumber || 'N/A'} | DLC: {stock.dlc ? dateToFrench(stock.dlc) : 'N/A'} | Dispo: {stock.quantity}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Zone {inv.inventoryId === 'MANUAL' ? '*' : '(lecture seule)'}</label>
                                                    <input
                                                        type="text"
                                                        value={inv.area}
                                                        disabled={inv.inventoryId !== 'MANUAL'}
                                                        onChange={(e) => {
                                                            if (inv.inventoryId === 'MANUAL') {
                                                                handleUpdateInventoryLine(invIndex, 'area', e.target.value);
                                                            }
                                                        }}
                                                        className={`w-full rounded-lg border border-gray-300 px-2 py-1 text-sm ${inv.inventoryId !== 'MANUAL' ? 'bg-gray-100' : 'focus:border-[#0260CB] focus:outline-none'}`}
                                                        placeholder={inv.inventoryId === 'MANUAL' ? 'Ex: A1, B2...' : ''}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Lot {inv.inventoryId === 'MANUAL' ? '' : '(lecture seule)'}</label>
                                                    <input
                                                        type="text"
                                                        value={inv.lotNumber || ''}
                                                        disabled={inv.inventoryId !== 'MANUAL'}
                                                        onChange={(e) => {
                                                            if (inv.inventoryId === 'MANUAL') {
                                                                handleUpdateInventoryLine(invIndex, 'lotNumber', e.target.value);
                                                            }
                                                        }}
                                                        className={`w-full rounded-lg border border-gray-300 px-2 py-1 text-sm ${inv.inventoryId !== 'MANUAL' ? 'bg-gray-100' : 'focus:border-[#0260CB] focus:outline-none'}`}
                                                        placeholder={inv.inventoryId === 'MANUAL' ? 'Numéro de lot' : ''}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">DLC {inv.inventoryId === 'MANUAL' ? '' : '(lecture seule)'}</label>
                                                    <input
                                                        type="text"
                                                        value={inv.dlc ? (inv.inventoryId === 'MANUAL' ? inv.dlc : dateToFrench(inv.dlc)) : ''}
                                                        disabled={inv.inventoryId !== 'MANUAL'}
                                                        onChange={(e) => {
                                                            if (inv.inventoryId === 'MANUAL') {
                                                                handleUpdateInventoryLine(invIndex, 'dlc', e.target.value);
                                                            }
                                                        }}
                                                        className={`w-full rounded-lg border border-gray-300 px-2 py-1 text-sm ${inv.inventoryId !== 'MANUAL' ? 'bg-gray-100' : 'focus:border-[#0260CB] focus:outline-none'}`}
                                                        placeholder={inv.inventoryId === 'MANUAL' ? 'JJ/MM/AAAA' : ''}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Dispo (lecture seule)</label>
                                                    <input
                                                        type="number"
                                                        value={inv.inventoryId === 'MANUAL' ? '-' : inv.quantityAvailable}
                                                        disabled
                                                        className="w-full rounded-lg border border-gray-300 bg-gray-100 px-2 py-1 text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Qté à prélever *</label>
                                                    <input
                                                        type="number"
                                                        value={inv.quantityPicked}
                                                        onChange={(e) => handleUpdateInventoryLine(invIndex, 'quantityPicked', parseFloat(e.target.value) || 0)}
                                                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-[#0260CB] focus:outline-none"
                                                        min="0"
                                                        max={inv.inventoryId === 'MANUAL' ? undefined : inv.quantityAvailable}
                                                        step="0.01"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                // Mode lecture
                                <>
                                    {isEditable && (
                                        <div className="mb-3 flex justify-end">
                                            <button
                                                onClick={() => handleEditInventoryLines(line.productId, line.inventoryLines)}
                                                className="linear flex items-center gap-1 rounded-lg bg-[#0260CB] px-3 py-1 text-sm text-white transition duration-200 hover:bg-[#01499b] active:bg-[#024086]"
                                            >
                                                <MdEdit size={16} />
                                                Modifier
                                            </button>
                                        </div>
                                    )}
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="pb-2 text-left text-sm font-medium text-gray-600">Zone</th>
                                                <th className="pb-2 text-left text-sm font-medium text-gray-600">Lot</th>
                                                <th className="pb-2 text-left text-sm font-medium text-gray-600">DLC</th>
                                                <th className="pb-2 text-right text-sm font-medium text-gray-600">Quantité prélevée</th>
                                                <th className="pb-2 text-right text-sm font-medium text-gray-600">Dispo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {line.inventoryLines.map((inv: IPreparationInventoryLine, invIndex: number) => (
                                                <tr key={invIndex} className="border-b border-gray-100">
                                                    <td className="py-3 text-sm text-navy-700 dark:text-white">
                                                        {inv.area}
                                                    </td>
                                                    <td className="py-3 text-sm text-navy-700 dark:text-white">
                                                        {inv.lotNumber || 'N/A'}
                                                    </td>
                                                    <td className="py-3 text-sm text-navy-700 dark:text-white">
                                                        {inv.dlc ? dateToFrench(inv.dlc) : 'N/A'}
                                                    </td>
                                                    <td className="py-3 text-right text-sm font-semibold text-navy-700 dark:text-white">
                                                        {inv.quantityPicked}
                                                    </td>
                                                    <td className="py-3 text-right text-sm text-gray-600">
                                                        {inv.quantityAvailable}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>

                        {line.notes && (
                            <div className="mt-4 rounded-lg bg-blue-50 p-3">
                                <p className="text-sm font-medium text-blue-900">Notes:</p>
                                <p className="text-sm text-blue-700">{line.notes}</p>
                            </div>
                        )}
                    </Card>
                            </div>
                        );
                    });
                })()}
            </div>

            {/* Validation Modal */}
            <ConfirmModal
                isOpen={showValidateModal}
                onClose={() => setShowValidateModal(false)}
                onConfirm={confirmValidate}
                title={validateModalContent.title}
                description={validateModalContent.description}
                confirmText="Valider"
                cancelText="Annuler"
                confirmColor={validateModalContent.isWarning ? "bg-orange-500" : "bg-green-500"}
                isWarning={validateModalContent.isWarning}
            />

            {/* Cancel Modal */}
            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={confirmCancel}
                title="Annuler la préparation"
                description="Êtes-vous sûr de vouloir annuler cette préparation ? Les données seront conservées mais le statut sera changé à 'Annulée'."
                confirmText="Annuler la préparation"
                cancelText="Retour"
                confirmColor="bg-red-500"
                isWarning={true}
            />
        </div>
    );
};

export default ViewPreparation;
